import {
    ClaimApi,
    CoreFetch,
    FacilityApi,
    ListSupportFacilitiesRequest,
    ListSupportProvidersRequest,
    ProviderApi,
    TagApi,
    TagType,
} from '../../api';
import { rosterApiPage } from './roster.api-page';
import { rosterCollectClaims } from './roster.claims-mapping';
import { rosterCall, rosterRawRequest } from './roster.raw-request';
import type { RosterApplyResult, RosterRecordRef } from './roster.types';
import type { RosterSupportClients } from './roster.support-source';

/**
 * The live `RosterSupportClients`: the generated support client behind `CoreFetch`, so requests
 * carry the session and pass the demo-mode guard like every other call (NS-14).
 *
 * Kept apart from `roster.support-source.ts` so that file — and its tests — never touch the
 * network layer.
 */
export function createRosterSupportClients(): RosterSupportClients {
    const providers = (): ProviderApi => CoreFetch<ProviderApi>(ProviderApi);
    const facilities = (): FacilityApi => CoreFetch<FacilityApi>(FacilityApi);
    const tags = (): TagApi => CoreFetch<TagApi>(TagApi);

    return {
        listProviders: (request: ListSupportProvidersRequest) =>
            rosterCall(async () => rosterApiPage(await providers().listSupportProvidersRaw(request)), 'GET'),
        getProvider: id => rosterCall(() => providers().getSupportProvider({ id }), 'GET'),
        createProvider: payload =>
            rosterCall(() => providers().createSupportProvider({ supportCreateProviderPayload: payload }), 'POST'),
        updateProvider: (id, payload) =>
            rosterCall(async () => {
                await providers().updateSupportProvider({ id, supportUpdateProviderPayload: payload });
            }, 'PUT'),
        disableProvider: id =>
            rosterCall(async () => {
                await providers().disableSupportProvider({ id });
            }, 'POST'),
        listFacilities: (request: ListSupportFacilitiesRequest) =>
            rosterCall(async () => rosterApiPage(await facilities().listSupportFacilitiesRaw(request)), 'GET'),
        getFacility: id => rosterCall(() => facilities().getSupportFacility({ id }), 'GET'),
        createFacility: payload =>
            rosterCall(() => facilities().createSupportFacility({ supportCreateFacilityPayload: payload }), 'POST'),
        updateFacility: (id, payload) =>
            rosterCall(async () => {
                await facilities().updateSupportFacility({ id, supportUpdateFacilityPayload: payload });
            }, 'PUT'),
        disableFacility: id =>
            rosterCall(async () => {
                await facilities().disableSupportFacility({ id });
            }, 'POST'),
        findTags: name =>
            rosterCall(
                () =>
                    tags().listSupportTags({
                        filterName: [`EQ:${name}`],
                        filterActive: 'EQ:1',
                        filterTagType: [`EQ:${TagType.USER}`],
                        limit: 100,
                    }),
                'GET',
            ),
        createTag: name =>
            rosterCall(() => tags().createSupportTag({ supportCreateTagPayload: { name, type: TagType.USER } }), 'POST'),
        // The deprecated route is the one the support app records tags with today (TagsStore.recordTag,
        // ClaimsStore) — proven in production; the newer one is not used anywhere yet.
        recordTag: (tagId, target) =>
            rosterCall(async () => {
                await tags().createSupportTagRecordDeprecated({ tid: tagId, supportCreateTagRecordPayload: target });
            }, 'POST'),
        listClaims: (ref: RosterRecordRef) =>
            rosterCollectClaims((offset, limit) =>
                rosterCall(
                    async () =>
                        rosterApiPage(
                            await CoreFetch<ClaimApi>(ClaimApi).listSupportClaimsRaw({
                                ...(ref.tab === 'providers'
                                    ? { filterProviderId: [`EQ:${ref.id}`] }
                                    : { filterFacilityId: [`EQ:${ref.id}`] }),
                                offset,
                                limit,
                            }),
                        ),
                    'GET',
                ),
            ),
        // Proposed route — no endpoint exists (docs/roster-backend-asks.md). NOT `sendClaim`: that
        // transmits to the payer, and re-sending unchanged claims is a duplicate submission.
        reapplyClaims: async (ref: RosterRecordRef, claimIds: string[], options: { retransmit: boolean }) =>
            (await rosterRawRequest('POST', `/support/${rosterRoute(ref)}/${encodeURIComponent(ref.id)}/claims/reapply`, {
                body: { claimIds, retransmit: options.retransmit },
            })) as RosterApplyResult,
    };
}

/** `provider` or `facility` — the route segment for a record's tab. */
export function rosterRoute(ref: RosterRecordRef): 'provider' | 'facility' {
    return ref.tab === 'providers' ? 'provider' : 'facility';
}
